/**
 * Zalo ZNS, the branch page of the notify module `zalo-zns` (subsystems/communications/facade/adapters/admin-api/src/admin/zns.rs):
 * the template registry, "Gửi thử", the message log, opt-outs. The rules are the notify
 * core's (modules/notify/core, `communications_notify::channels`). The connection itself (app id, OA id, app
 * secret, refresh token; versions, "Kiểm tra kết nối") is the module's instance in Cài đặt › Module ›
 * Thông báo (the module host).
 */

import type { CallOptions, ISODateTime, WriteOptions } from '../types';
import type { ModuleHealth } from './modules';

/** The module of this page. */
export const ZNS_MODULE = 'zalo-zns';

export type ZnsMessageStatus = 'PENDING' | 'SENDING' | 'SENT' | 'DELIVERED' | 'FAILED' | 'UNKNOWN' | 'SUPPRESSED';
export type ZnsEmailMode = 'ALSO' | 'FALLBACK';
/** The token chain's state (modules/notify/zalo-zns/src/chain.rs). */
export type ZnsChainState = 'READY' | 'REFRESH_REFUSED' | 'REFRESH_UNKNOWN';

/** The OA's token chain as the module shows it (never a token). */
export interface ZnsChain {
  state: ZnsChainState;
  has_access_token: boolean;
  access_expires_at: ISODateTime | null;
  refreshed_at: ISODateTime | null;
  refresh_count: number;
  next_refresh_at: ISODateTime | null;
  /** A chain code (`REFRESH_REFUSED:-14014`, `NOT_REACHED`, …). */
  last_error_code: string | null;
  last_error_at: ISODateTime | null;
  /** The configuration generation that started the chain. */
  started_by_generation: number;
}

/** The module's own view of its connection (its `connection` admin action). */
export interface ZnsConnection {
  chain: ZnsChain | null;
  /** The chain started from the refresh token of the version in force (false: a previous version's). */
  chain_is_this_version: boolean;
  quota_daily: number | null;
  quota_remaining: number | null;
  quota_at: ISODateTime | null;
  /** Zalo's code of the last problem of the whole connection (balance, permissions…). */
  last_problem_code: string | null;
  last_problem_at: ISODateTime | null;
  development: boolean;
}

export interface ZnsOverview {
  /** An instance of the module has a version in force. */
  configured: boolean;
  enabled: boolean;
  version: number | null;
  /** A test deployment: only the Zalo app's and OA's administrators receive messages. */
  development: boolean;
  /** How long an e-mail sent only when the message fails waits (the notify core's setting). */
  email_fallback_minutes: number;
  connection: ZnsConnection | null;
  health: { state: ModuleHealth; reason?: string } | null;
  counts_7d: Partial<Record<ZnsMessageStatus, number>>;
}

/** A parameter's value: a source of the notification or a fixed text. */
export type ZnsParamValue = { source: string } | { text: string };

export interface ProviderTemplateParam {
  name: string;
  required: boolean;
  max_len?: number | null;
  min_len?: number | null;
  /** The provider's type name (`STRING`, `NUMBER`, …). */
  kind?: string | null;
  accept_null?: boolean | null;
}

/** The provider's (Zalo's) description of a template. */
export interface ProviderTemplate {
  provider_template_id: string;
  name: string | null;
  /** Zalo's own status (`ENABLE`, `PENDING_REVIEW`, `REJECT`, …). */
  status: string | null;
  /** Whether Zalo lets the template be sent now. */
  usable: boolean | null;
  params: ProviderTemplateParam[];
  tag: string | null;
  price: string | null;
  preview_url: string | null;
}

export interface ZnsMapping {
  id: string;
  enabled: boolean;
  provider_template_id: string;
  params: Record<string, ZnsParamValue>;
  email_mode: ZnsEmailMode;
  provider_template: ProviderTemplate | null;
  provider_checked_at: ISODateTime | null;
  version: number;
  updated_by: string | null;
  updated_at: ISODateTime | null;
}

export interface ZnsTemplateItem {
  /** The notification (template key). */
  key: string;
  fallback_allowed: boolean;
  /** The sources a parameter may take for this notification. */
  sources: string[];
  /** Sample variables of the notification ("Gửi thử" uses them). */
  sample: Record<string, unknown>;
  mapping: ZnsMapping | null;
}

export interface ZnsMappingInput {
  enabled: boolean;
  provider_template_id: string;
  params: Record<string, ZnsParamValue>;
  email_mode: ZnsEmailMode;
  expected_version?: number;
}

export interface ZnsMappingError {
  param: string;
  code: string;
}

export interface ZnsTestResult {
  message_id: string;
  status: ZnsMessageStatus;
  /** The notify branch's code (`RECIPIENT_INVALID`, `NO_ANSWER`, …). */
  error_code: string | null;
  /** Zalo's own code (`-108`). */
  provider_code: string | null;
  provider_message_id: string | null;
  test_mode: boolean;
}

export interface ZnsMessageRow {
  id: string;
  purpose: 'NOTIFICATION' | 'TEST';
  module: string;
  instance_key: string;
  template_key: string;
  provider_template_id: string;
  phone: string;
  status: ZnsMessageStatus;
  error_code: string | null;
  provider_code: string | null;
  attempts: number;
  max_attempts: number;
  next_attempt_at: ISODateTime;
  email_mode: ZnsEmailMode;
  tracking_id: string;
  provider_message_id: string | null;
  test_mode: boolean;
  sent_at: ISODateTime | null;
  delivered_at: ISODateTime | null;
  created_at: ISODateTime;
  requested_by: string;
  notification_id: string | null;
  notification_status: string | null;
  customer_id: string | null;
  customer_name: string | null;
  domain_id: string | null;
  domain: string | null;
}

export interface ZnsMessageDetail extends ZnsMessageRow {
  template_data: Record<string, string>;
  mapping_version: number | null;
  status_checks: number;
  provider_answer: string | null;
}

export interface ZnsMessageQuery {
  status?: ZnsMessageStatus;
  template_key?: string;
  phone?: string;
  purpose?: 'NOTIFICATION' | 'TEST';
  notification_id?: string;
  limit?: number;
  before?: string;
}

export interface ZnsOptOut {
  id: string;
  phone: string;
  /** The channel module the entry is for; `null`: every phone channel (staff). */
  module: string | null;
  /** STAFF: a customer asked; CHANNEL: the provider said the user refuses the sender. */
  source: 'STAFF' | 'CHANNEL';
  reason: string | null;
  created_by: string;
  created_at: ISODateTime;
  withdrawn_at: ISODateTime | null;
  withdrawn_by: string | null;
}

export interface ZnsApi {
  getZnsOverview(opts?: CallOptions): Promise<ZnsOverview>;
  listZnsTemplates(opts?: CallOptions): Promise<{ items: ZnsTemplateItem[]; never: string[] }>;
  saveZnsTemplate(key: string, body: ZnsMappingInput, opts?: WriteOptions): Promise<{ key: string; mapping: ZnsMapping }>;
  deleteZnsTemplate(key: string, opts?: WriteOptions): Promise<{ deleted: boolean }>;
  describeZnsTemplate(key: string, body: { provider_template_id?: string }, opts?: WriteOptions): Promise<{ template: ProviderTemplate; mapping_errors: ZnsMappingError[] }>;
  testZnsTemplate(key: string, body: { phone: string; draft?: { provider_template_id: string; params: Record<string, ZnsParamValue> } }, opts?: WriteOptions): Promise<ZnsTestResult>;
  listZnsMessages(query: ZnsMessageQuery, opts?: CallOptions): Promise<{ items: ZnsMessageRow[]; next_before: string | null }>;
  getZnsMessage(id: string, opts?: CallOptions): Promise<ZnsMessageDetail>;
  resendZnsMessage(id: string, opts?: WriteOptions): Promise<{ queued: boolean }>;
  listZnsOptOuts(query: { all?: boolean }, opts?: CallOptions): Promise<{ items: ZnsOptOut[] }>;
  addZnsOptOut(body: { phone: string; reason?: string }, opts?: WriteOptions): Promise<{ added: boolean; id: string | null; phone: string }>;
  withdrawZnsOptOut(id: string, opts?: WriteOptions): Promise<{ withdrawn: boolean }>;
}
